import Button from '@ui/Button';
import { slugify } from '@util/urlHelpers';
import { useWarehouse } from 'app/product/[slug]/components/WarehouseProvider';
import { ProductDocument } from 'models/product';
import Link from 'next/link';
import { useMemo, useState } from 'react';

export default function PartNumbersTab({
  product,
}: {
  product: ProductDocument;
}) {
  const { isActive, mode, relatedItems, isLoading, useGroupTitle } =
    useWarehouse();
  const [showAllPartNumbers, setShowAllPartNumbers] = useState(false);

  // Group MPN table for card-browse, or when the user expands via the button.
  const inGroupBrowse = useGroupTitle && mode !== 'specific';

  const groupRows = useMemo(
    () => buildGroupPartNumberRows(relatedItems),
    [relatedItems]
  );
  const hasFits =
    isActive &&
    groupRows.some(
      (row) => row.fitment && row.fitment !== '—' && row.fitment !== 'Universal'
    );
  const showGroupTable =
    (inGroupBrowse || showAllPartNumbers) && groupRows.length > 0;
  const canSeeAllPartNumbers =
    !inGroupBrowse && groupRows.length > 1 && !showAllPartNumbers;

  if ((inGroupBrowse || showAllPartNumbers) && isLoading && !groupRows.length) {
    return (
      <div className="flex h-[10rem] w-full items-center justify-center px-8 py-16">
        <span className="text-[1.6rem] font-medium text-zinc-500">
          Loading part numbers…
        </span>
      </div>
    );
  }

  if (showGroupTable) {
    return (
      <div className="py-8">
        <GroupPartNumberTable rows={groupRows} hasFits={hasFits} />
        {showAllPartNumbers && !inGroupBrowse ? (
          <div className="mt-4 flex justify-center px-8 sm:px-0">
            <Button
              type="underline"
              height="small"
              onClick={() => setShowAllPartNumbers(false)}
            >
              Show This Part Only
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mt-8 hidden sm:flex">
        {product.variations?.length &&
        product.variations.some((v) => v.mpn?.length) ? (
          <div className="relative max-h-[40rem] w-full overflow-y-scroll border-[1px] border-zinc-200">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="sticky top-0 border-r border-[#D9D9D9] bg-[#222] p-6 text-left text-[1.8rem] font-semibold text-white">
                    Product Style
                  </th>
                  <th className="sticky top-0 bg-[#222] p-6 text-left text-[1.8rem] font-semibold text-white">
                    MPN
                  </th>
                </tr>
              </thead>
              <tbody>
                {product.variations
                  .sort((a, b) =>
                    a.mpn?.length && b.mpn?.length
                      ? a.mpn[0].localeCompare(b.mpn[0])
                      : 0
                  )
                  .map((variation, index) => {
                    if (!variation.mpn?.length) return null;
                    return (
                      <tr
                        key={`${variation.size}-${variation.color}-${index}`}
                        className={`h-[5rem] border-y border-[#D9D9D9] ${
                          index % 2 === 0 ? 'bg-transparent' : 'bg-[#F6F6F6]'
                        }`}
                      >
                        <td className="break-words border-r border-[#D9D9D9] px-2 pl-4 text-[1.8rem]">
                          {variation.size !== 'N/A' ? variation.size : ''}
                          {variation.size !== 'N/A' && variation.color
                            ? ' / '
                            : ''}
                          {variation.color ? variation.color : ''}
                        </td>
                        <td className="break-words px-2 pl-8 text-[1.8rem]">
                          <Link
                            target={'_blank'}
                            className="underline"
                            href={`/product/${slugify(product.title)}/${
                              variation.mpn[0]
                            }`}
                          >
                            {variation.mpn[0]}
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : product.mpn?.length ? (
          <div className="relative max-h-[40rem] w-full overflow-y-scroll border-[1px] border-zinc-200">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="sticky top-0 border-r border-[#D9D9D9] bg-[#222] p-6 text-left text-[1.8rem] font-semibold text-white">
                    MPN
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  className={
                    'h-[5rem] border-y border-[#D9D9D9] bg-transparent'
                  }
                >
                  <td className="break-words border-r border-[#D9D9D9] px-2 pl-4 text-[1.8rem]">
                    <Link
                      target={'_blank'}
                      className="underline"
                      href={`/product/${slugify(product.title)}/${
                        product.mpn[0]
                      }`}
                    >
                      {product.mpn[0]}
                    </Link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : product.sku ? (
          <div className="flex flex-row">
            <p className="w-[12rem] text-[1.6rem]">SKU:</p>
            <span className="grow text-[1.6rem] font-semibold text-brand-black">
              {product.sku}
            </span>
          </div>
        ) : null}
      </div>

      <div className="mt-8 flex w-full sm:hidden">
        {product.variations?.length ? (
          <div className="flex w-full flex-col">
            {product.variations.map((variation, index) => (
              <div
                className={`${
                  index % 2 == 0 ? 'bg-white' : 'bg-[#F6F6F6]'
                } flex h-[18rem] w-full flex-col gap-y-8 border-[1.5px] border-[#D9D9D9] p-8`}
                key={`${variation.size}-${variation.color}-${index}`}
              >
                <div className="flex flex-col gap-y-1">
                  <span className="text-[1.8rem] font-medium text-brand-primary">
                    Product Style
                  </span>
                  <span className="text-[1.8rem] font-semibold text-black">
                    {variation.color ?? ''} / {variation.size ?? ''}
                  </span>
                </div>
                {!!variation.mpn?.length ? (
                  <div className="flex flex-col gap-y-1">
                    <span className="text-[1.8rem] font-medium text-brand-primary">
                      MPN
                    </span>
                    <span className="text-[1.8rem] font-semibold text-black">
                      {variation.mpn[0]}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-y-1">
                    <span className="text-[1.8rem] font-medium text-brand-primary">
                      SKU Number
                    </span>
                    <span className="text-[1.8rem] font-semibold text-black">
                      {variation.sku ?? 'N/A'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : product.mpn?.length ? (
          <div className="flex w-full flex-col">
            <div className="flex h-[18rem] w-full flex-col gap-y-8 border-[1.5px] border-[#D9D9D9] p-8">
              <div className="flex flex-col gap-y-1">
                <span className="text-[1.8rem] font-medium text-brand-primary">
                  MPN
                </span>
                <span className="text-[1.8rem] font-semibold text-black">
                  <Link
                    target={'_blank'}
                    className="underline"
                    href={`/product/${slugify(product.title)}/${
                      product.mpn[0]
                    }`}
                  >
                    {product.mpn[0]}
                  </Link>
                </span>
              </div>
            </div>
          </div>
        ) : product.sku ? (
          <div className="flex flex-row">
            <p className="w-[12rem] text-[1.6rem]">SKU:</p>
            <span className="grow text-[1.6rem] font-semibold text-brand-black">
              <Link
                target={'_blank'}
                className="underline"
                href={`/product/${slugify(product.title)}/${product.sku}`}
              >
                {product.sku}
              </Link>
            </span>
          </div>
        ) : null}
      </div>

      {canSeeAllPartNumbers ? (
        <div className="mt-4 flex justify-center">
          <Button
            type="underline"
            height="small"
            onClick={() => setShowAllPartNumbers(true)}
          >
            See All Part Numbers
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function GroupPartNumberTable({
  rows,
  hasFits,
}: {
  rows: { mpn: string; fitment: string }[];
  hasFits: boolean;
}) {
  return (
    <>
      <div className="mt-8 hidden sm:flex">
        <div className="w-full border-[1px] border-zinc-200">
          <table className="w-full">
            <thead>
              <tr>
                <th
                  className={`${
                    hasFits ? 'border-r border-[#D9D9D9]' : ''
                  } bg-[#222] p-6 text-left text-[1.8rem] font-semibold text-white`}
                >
                  MPN
                </th>
                {hasFits && (
                  <th className="bg-[#222] p-6 text-left text-[1.8rem] font-semibold text-white">
                    Fits
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={row.mpn}
                  className={`h-[5rem] border-y border-[#D9D9D9] ${
                    index % 2 === 0 ? 'bg-transparent' : 'bg-[#F6F6F6]'
                  }`}
                >
                  <td
                    className={`break-words ${
                      hasFits ? 'border-r border-[#D9D9D9]' : ''
                    } px-2 pl-4 text-[1.8rem] font-medium`}
                  >
                    {row.mpn}
                  </td>
                  {hasFits && (
                    <td className="break-words px-2 pl-4 text-[1.8rem] text-zinc-700">
                      {row.fitment}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 flex w-full flex-col sm:hidden">
        {rows.map((row, index) => (
          <div
            key={row.mpn}
            className={`${
              index % 2 === 0 ? 'bg-white' : 'bg-[#F6F6F6]'
            } flex w-full flex-col gap-y-6 border-[1.5px] border-[#D9D9D9] p-8`}
          >
            <div className="flex flex-col gap-y-1">
              <span className="text-[1.8rem] font-medium text-brand-primary">
                MPN
              </span>
              <span className="text-[1.8rem] font-semibold text-black">
                {row.mpn}
              </span>
            </div>
            {hasFits && (
              <div className="flex flex-col gap-y-1">
                <span className="text-[1.8rem] font-medium text-brand-primary">
                  Fits
                </span>
                <span className="text-[1.6rem] text-zinc-700">
                  {row.fitment}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function summarizeItemFitment(item: ProductDocument): string {
  const bikes = item.fitting_bikes?.bikes ?? [];
  if (!bikes.length) {
    return item.fitting_bikes?.universal ? 'Universal' : '—';
  }

  const combos = new Map<string, { label: string; years: number[] }>();
  for (const bike of bikes) {
    if (!bike.make || !bike.model) continue;
    const key = `${bike.make}|${bike.model}`;
    const entry = combos.get(key) ?? {
      label: `${bike.make} ${bike.model}`,
      years: [],
    };
    if (bike.year) entry.years.push(bike.year);
    combos.set(key, entry);
  }

  const format = ({ label, years }: { label: string; years: number[] }) => {
    const sorted = [...years].sort((a, b) => a - b);
    if (!sorted.length) return label;
    const range =
      sorted.length > 1
        ? `${sorted[0]}-${sorted[sorted.length - 1]}`
        : `${sorted[0]}`;
    return `${label} ${range}`;
  };

  const formatted = Array.from(combos.values()).map(format);
  if (formatted.length <= 2) return formatted.join(', ');
  return `${formatted.slice(0, 2).join(', ')} +${formatted.length - 2} more`;
}

function buildGroupPartNumberRows(
  relatedItems: ProductDocument[]
): { mpn: string; fitment: string }[] {
  const seen = new Set<string>();
  const rows: { mpn: string; fitment: string }[] = [];

  for (const item of relatedItems) {
    for (const mpn of item.mpn ?? []) {
      if (!mpn || seen.has(mpn)) continue;
      seen.add(mpn);
      rows.push({
        mpn,
        fitment: summarizeItemFitment(item),
      });
    }
  }

  return rows.sort((a, b) => a.mpn.localeCompare(b.mpn));
}
